import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';

import {
  Footer,
  Header,
  PageWrapper,
  SectionTitle,
  Spacer,
} from '../../components';
import { cockpitGet } from '../../config/cockpit';
import { useHeader } from '../../contexts/useHeader';
import { privacyLinks } from '../../data/mock.data.privacy';
import ErrorPage404 from '../404';

interface WebsiteLegalsData {
  WebsitePrivacyPolicy?: string;
  WebsiteTermsandConditions?: string;
  WebsiteCookiePolicy?: string;
}

type LegalType =
  | 'privacy-policy'
  | 'terms-and-conditions'
  | 'cookie-policy';

const validLegalTypes: LegalType[] = [
  'privacy-policy',
  'terms-and-conditions',
  'cookie-policy',
];

const richTextSx = {
  width: '100%',
  color: 'gray.500',
  fontFamily: 'inherit',

  fontSize: {
    base: '12px',
    md: '14px',
  },

  fontWeight: 300,
  lineHeight: 1.8,

  '& p': {
    margin: 0,
    marginBottom: '16px',
  },

  '& p + p': {
    marginTop: '12px',
  },

  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 700,
    marginTop: '30px',
    marginBottom: '14px',
  },

  '& ul, & ol': {
    paddingLeft: '24px',
    marginTop: '16px',
    marginBottom: '16px',
  },

  '& li': {
    marginBottom: '8px',
  },

  '& strong, & b': {
    fontWeight: 700,
  },

  '& em, & i': {
    fontStyle: 'italic',
  },

  '& a': {
    textDecoration: 'underline',
  },
};

const LegalsPage = () => {
  const { type } = useParams<{
    type: string;
  }>();

  const { headerDimensions } = useHeader();

  const [legalData, setLegalData] =
    useState<WebsiteLegalsData | null>(null);

  const [loading, setLoading] =
    useState(true);

  const isValidLegalType =
    !!type &&
    validLegalTypes.includes(type as LegalType);

  useEffect(() => {
    if (!isValidLegalType) {
      setLoading(false);

      return;
    }

    cockpitGet<WebsiteLegalsData>(
      '/api/content/item/WebsiteLegals'
    )
      .then((data) => {
        setLegalData(data);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to load legal content from Cockpit:',
          err
        );

        setLegalData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isValidLegalType]);

  const legalContent = useMemo(() => {
    if (!legalData || !type) {
      return null;
    }

    switch (type as LegalType) {
      case 'privacy-policy':
        return {
          title: 'Privacy Policy',
          content:
            legalData.WebsitePrivacyPolicy || '',
        };

      case 'terms-and-conditions':
        return {
          title: 'Terms and Conditions',
          content:
            legalData.WebsiteTermsandConditions || '',
        };

      case 'cookie-policy':
        return {
          title: 'Cookie Policy',
          content:
            legalData.WebsiteCookiePolicy || '',
        };

      default:
        return null;
    }
  }, [legalData, type]);

  if (!isValidLegalType) {
    return <ErrorPage404 />;
  }

  if (!loading && !legalContent) {
    return <ErrorPage404 />;
  }

  return (
    <>
      <Header
        links={privacyLinks}
        justLightMode
      />

      <PageWrapper>
        <Flex
          width="100%"
          minHeight="100vh"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
        >
          <Spacer
            size={headerDimensions?.height || 0}
          />

          <Box
            width={{
              base: '100%',
              md: '80%',
            }}
            paddingX={{
              base: '20px',
              md: 0,
            }}
            paddingTop={{
              base: '20px',
              md: '40px',
            }}
            paddingBottom={{
              base: '60px',
              md: '100px',
            }}
          >
            {loading ? (
              <Flex
                width="100%"
                justifyContent="center"
                paddingY="100px"
              >
                <Spinner />
              </Flex>
            ) : legalContent ? (
              <>
                <SectionTitle
                  title={legalContent.title}
                  noPadding
                />

                {legalContent.content ? (
                  <Box
                    marginTop="30px"
                    sx={richTextSx}
                    dangerouslySetInnerHTML={{
                      __html:
                        legalContent.content,
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </Box>
        </Flex>
      </PageWrapper>

      <Footer />
    </>
  );
};

export default LegalsPage;